import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect } from 'react';
import { StandardForm, FormField, FormLabel, SearchInput } from '@vp/swan';
import { useSearchContext } from '../../searchContext/SearchContextProvider.js';
import debounce from 'lodash.debounce';
import { ANALYTICS_WRAPPER_CLASS, SEARCH_BAR_ENGAGED_EVENT, SEARCH_BAR_ENGAGED_EVENT_TYPED_LABEL, SEARCH_BAR_ENGAGED_EVENT_FOCUS_LABEL } from '../../util/analyticsConstants.js';
import localize from '../../util/localize.js';
import { useRecentSearch } from '../../localStorage/useRecentSearch.js';
import { PRODUCT_SEARCH_ON_FOCUS_CONTENT_VARIATION_CONTROL, PRODUCT_SEARCH_ON_FOCUS_CONTENT } from '../../util/abTestConstants.js';
import { SEARCH_BAR_FLYOUT_APPLICATION_NAME } from '../../util/constants.js';

var OmniSearchInput = function OmniSearchInput(_ref) {
  var searchLabel = _ref.searchLabel,
    searchPlaceholder = _ref.searchPlaceholder;
  var _useSearchContext = useSearchContext(),
    locale = _useSearchContext.locale,
    query = _useSearchContext.query,
    setQuery = _useSearchContext.setQuery,
    setIsFlyoutExpanded = _useSearchContext.setIsFlyoutExpanded,
    handleKeyDown = _useSearchContext.handleKeyDown,
    allResultsUrl = _useSearchContext.allResultsUrl,
    instanceId = _useSearchContext.instanceId,
    activeResultId = _useSearchContext.activeResultId,
    setSearchInputHasBeenTouched = _useSearchContext.setSearchInputHasBeenTouched,
    checkFocus = _useSearchContext.checkFocus,
    isFlyoutExpanded = _useSearchContext.isFlyoutExpanded,
    fireImpression = _useSearchContext.fireImpression,
    applicationName = _useSearchContext.applicationName;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    searchEngaged = _useState2[0],
    setSearchEngaged = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    searchFocusEngaged = _useState4[0],
    setSearchFocusEngaged = _useState4[1];
  var _useRecentSearch = useRecentSearch(),
    addRecentSearch = _useRecentSearch.addRecentSearch;
  var _useState5 = useState(query || ''),
    _useState6 = _slicedToArray(_useState5, 2),
    currentQuery = _useState6[0],
    setCurrentQuery = _useState6[1];
  useEffect(function () {
    var _document;
    var params = ((_document = document) === null || _document === void 0 ? void 0 : _document.location.search) && new URLSearchParams(document.location.search);
    var query = params ? (params === null || params === void 0 ? void 0 : params.has('query')) && (params === null || params === void 0 ? void 0 : params.get('query')) : '';
    if (query) {
      setQuery(query);
    }
  }, []);
  useEffect(function () {
    setCurrentQuery(query);
  }, [query]);
  var innerHandleChange = function innerHandleChange(evt) {
    setSearchInputHasBeenTouched(true);
    if (window.tracking && !searchEngaged) {
      handleSearchBarEngaged(evt, SEARCH_BAR_ENGAGED_EVENT_TYPED_LABEL);
      setSearchEngaged(true);
    }
    setQuery(evt.target.value);
  };
  var debouncedHandleChange = React__default.useRef(debounce(function (evt) {
    innerHandleChange(evt);
  }, 200, {
    maxWait: 1000
  })).current;
  React__default.useEffect(function () {
    return function () {
      debouncedHandleChange.cancel();
    };
  }, [debouncedHandleChange]);
  var handleChange = function handleChange(evt) {
    setCurrentQuery(evt.target.value);
    debouncedHandleChange(evt);
  };
  var handleFocus = function handleFocus(evt) {
    if (window.tracking && !searchFocusEngaged) {
      handleSearchBarEngaged(evt, SEARCH_BAR_ENGAGED_EVENT_FOCUS_LABEL);
      setSearchFocusEngaged(true);
      if (applicationName === SEARCH_BAR_FLYOUT_APPLICATION_NAME) {
        fireImpression({
          Experiment: PRODUCT_SEARCH_ON_FOCUS_CONTENT,
          Variation: PRODUCT_SEARCH_ON_FOCUS_CONTENT_VARIATION_CONTROL
        });
      }
    }
    checkFocus();
  };
  var handleSearchBarEngaged = function handleSearchBarEngaged(evt, label) {
    var _evt$currentTarget, _appRoot$dataset;
    var target = (_evt$currentTarget = evt.currentTarget) !== null && _evt$currentTarget !== void 0 ? _evt$currentTarget : evt.target;
    var appRoot = target === null || target === void 0 ? void 0 : target.closest(".".concat(ANALYTICS_WRAPPER_CLASS));
    var application = appRoot === null || appRoot === void 0 || (_appRoot$dataset = appRoot.dataset) === null || _appRoot$dataset === void 0 ? void 0 : _appRoot$dataset.searchApplicationName;
    window.tracking.track(SEARCH_BAR_ENGAGED_EVENT, {
      application: application,
      location: window.location.pathname,
      label: label
    });
  };
  var queryParams = new URL(allResultsUrl, 'http://www.does-not-matter.com').searchParams;
  var placeholder = searchPlaceholder || localize('searchPlaceholder', locale);
  return /*#__PURE__*/React__default.createElement("div", {
    className: "search-group",
    role: "group"
  }, /*#__PURE__*/React__default.createElement("search", null, /*#__PURE__*/React__default.createElement(StandardForm, {
    action: allResultsUrl,
    onSubmit: function onSubmit(evt) {
      if (!currentQuery) {
        evt.preventDefault();
        return;
      }
      setQuery(currentQuery);
      addRecentSearch(currentQuery);
      if (window.tracking) {
        var _appRoot$dataset2;
        var appRoot = evt.currentTarget.closest(".".concat(ANALYTICS_WRAPPER_CLASS));
        var application = appRoot === null || appRoot === void 0 || (_appRoot$dataset2 = appRoot.dataset) === null || _appRoot$dataset2 === void 0 ? void 0 : _appRoot$dataset2.searchApplicationName;
        window.tracking.track('Search Submitted', {
          query: currentQuery,
          application: application,
          location: window.location.pathname
        });
      }
    }
  }, Array.from(queryParams.keys()).map(function (key, index) {
    return /*#__PURE__*/React__default.createElement("input", {
      type: "hidden",
      name: key,
      value: queryParams.get(key),
      key: key + index
    });
  }), /*#__PURE__*/React__default.createElement(FormField, {
    className: "search-bar-form-field"
  }, /*#__PURE__*/React__default.createElement(FormLabel, {
    visuallyHidden: true
  }, searchLabel || localize('searchLabel', locale)), /*#__PURE__*/React__default.createElement(SearchInput, {
    className: "search-bar-input",
    role: "combobox",
    accessibleTextForClearButton: localize('clearLabel', locale),
    accessibleTextForSearchButton: placeholder,
    placeholder: placeholder,
    value: currentQuery,
    "aria-controls": "siteSearchResultsListBox-".concat(instanceId),
    "aria-activedescendant": activeResultId,
    "aria-expanded": isFlyoutExpanded !== null && isFlyoutExpanded !== void 0 ? isFlyoutExpanded : false,
    onChange: handleChange,
    onKeyDown: function onKeyDown(evt) {
      if (evt.code === 'Escape') {
        setIsFlyoutExpanded(false);
      }
      handleKeyDown(evt);
    },
    onClear: function onClear() {
      setQuery('');
      setSearchInputHasBeenTouched(true);
    },
    onFocus: function onFocus(evt) {
      handleFocus(evt);
    },
    onBlur: function onBlur() {
      checkFocus();
    }
  })))));
};

export { OmniSearchInput as default };
//# sourceMappingURL=SearchInput.js.map
